/**
 * Chip construction — turns a filter set into human-readable, clearable chips.
 *
 * Split out of `ActiveFilterChips.tsx` so that file only exports a component
 * (React Fast Refresh degrades when a module mixes components with other
 * exports).
 */

import type { BookingFilters, QuoteFilters } from '@limo/shared'
import type { FilterChip } from './ActiveFilterChips'


// =============================================================================

const fmtDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })

/**
 * Describe a half-open range in inclusive terms.
 * Stored [Aug 1, Sep 1) reads to a human as "Aug 1 – Aug 31".
 */
function describeRange(from?: string, to?: string): string {
  if (from && to) {
    const lastDay = new Date(new Date(to).getTime() - 86_400_000).toISOString()
    const start = fmtDate(from)
    const end = fmtDate(lastDay)
    return start === end ? start : `${start} – ${end}`
  }
  if (from) return `from ${fmtDate(from)}`
  return `before ${fmtDate(to!)}`
}

const money = (n: number): string => `$${n.toLocaleString()}`

function describeNumberRange(min?: number, max?: number, currency = false): string {
  const f = currency ? money : (v: number) => String(v)
  if (min !== undefined && max !== undefined) return `${f(min)}–${f(max)}`
  if (min !== undefined) return `≥ ${f(min)}`
  return `≤ ${f(max!)}`
}

/** Map option values to their display labels for chip text. */
function labelsFor(
  values: readonly string[],
  options: readonly { readonly value: string; readonly label: string }[]
): string {
  const labels = values.map((v) => options.find((o) => o.value === v)?.label ?? v)
  if (labels.length <= 2) return labels.join(', ')
  return `${labels.slice(0, 2).join(', ')} +${labels.length - 2}`
}

export interface ChipBuilderContext {
  readonly bookingStatusOptions: readonly { readonly value: string; readonly label: string }[]
  readonly paymentStatusOptions: readonly { readonly value: string; readonly label: string }[]
  readonly serviceTypeOptions: readonly { readonly value: string; readonly label: string }[]
  readonly quoteStatusOptions: readonly { readonly value: string; readonly label: string }[]
  readonly vehicleOptions: readonly { readonly value: string; readonly label: string }[]
}

/**
 * Build chips for whichever filters are set.
 *
 * `patch` applies a partial update, so each chip's clear action simply unsets its
 * own keys and leaves the rest of the filter set alone.
 */
export function buildFilterChips(
  filters: BookingFilters & QuoteFilters,
  patch: (next: Partial<BookingFilters & QuoteFilters>) => void,
  ctx: ChipBuilderContext
): readonly FilterChip[] {
  const chips: FilterChip[] = []
  const add = (key: string, label: string, clear: () => void): void => {
    chips.push({ key, label, clear })
  }

  if (filters.search) {
    add('search', `Search: "${filters.search}"`, () => patch({ search: undefined }))
  }

  if (filters.pickupDate?.from || filters.pickupDate?.to) {
    add(
      'pickupDate',
      `Pickup: ${describeRange(filters.pickupDate.from, filters.pickupDate.to)}`,
      () => patch({ pickupDate: undefined })
    )
  }

  if (filters.createdDate?.from || filters.createdDate?.to) {
    add(
      'createdDate',
      `Created: ${describeRange(filters.createdDate.from, filters.createdDate.to)}`,
      () => patch({ createdDate: undefined })
    )
  }

  if (filters.bookingStatus?.length) {
    add(
      'bookingStatus',
      `Status: ${labelsFor(filters.bookingStatus, ctx.bookingStatusOptions)}`,
      () => patch({ bookingStatus: undefined })
    )
  }

  if (filters.paymentStatus?.length) {
    add(
      'paymentStatus',
      `Payment: ${labelsFor(filters.paymentStatus, ctx.paymentStatusOptions)}`,
      () => patch({ paymentStatus: undefined })
    )
  }

  if (filters.status?.length) {
    add('status', `Status: ${labelsFor(filters.status, ctx.quoteStatusOptions)}`, () =>
      patch({ status: undefined })
    )
  }

  if (filters.amountMin !== undefined || filters.amountMax !== undefined) {
    add(
      'amount',
      `Total: ${describeNumberRange(filters.amountMin, filters.amountMax, true)}`,
      () => patch({ amountMin: undefined, amountMax: undefined })
    )
  }

  if (filters.hasBalanceDue) {
    add('hasBalanceDue', 'Has balance due', () => patch({ hasBalanceDue: undefined }))
  }

  if (filters.serviceType?.length) {
    add('serviceType', `Service: ${labelsFor(filters.serviceType, ctx.serviceTypeOptions)}`, () =>
      patch({ serviceType: undefined })
    )
  }

  if (filters.vehicleIds?.length) {
    add('vehicleIds', `Vehicle: ${labelsFor(filters.vehicleIds, ctx.vehicleOptions)}`, () =>
      patch({ vehicleIds: undefined })
    )
  }

  if (filters.passengerMin !== undefined || filters.passengerMax !== undefined) {
    add(
      'passengers',
      `Passengers: ${describeNumberRange(filters.passengerMin, filters.passengerMax)}`,
      () => patch({ passengerMin: undefined, passengerMax: undefined })
    )
  }

  if (filters.roundTrip !== undefined) {
    add('roundTrip', filters.roundTrip ? 'Round trip only' : 'One way only', () =>
      patch({ roundTrip: undefined })
    )
  }

  if (filters.airportPickup !== undefined) {
    add('airportPickup', filters.airportPickup ? 'Airport pickup' : 'Non-airport pickup', () =>
      patch({ airportPickup: undefined })
    )
  }

  if (filters.airportDropoff !== undefined) {
    add('airportDropoff', filters.airportDropoff ? 'Airport dropoff' : 'Non-airport dropoff', () =>
      patch({ airportDropoff: undefined })
    )
  }

  if (filters.isOperatorPriced !== undefined) {
    add('isOperatorPriced', filters.isOperatorPriced ? 'Operator priced' : 'Not operator priced', () =>
      patch({ isOperatorPriced: undefined })
    )
  }

  if (filters.isFlatRate !== undefined) {
    add('isFlatRate', filters.isFlatRate ? 'Flat rate' : 'Not flat rate', () =>
      patch({ isFlatRate: undefined })
    )
  }

  if (filters.hasDiscount !== undefined) {
    add('hasDiscount', filters.hasDiscount ? 'Has discount' : 'No discount', () =>
      patch({ hasDiscount: undefined })
    )
  }

  for (const [key, prefix] of [
    ['utmSource', 'Source'],
    ['utmMedium', 'Medium'],
    ['utmCampaign', 'Campaign'],
  ] as const) {
    const values = filters[key]
    if (values?.length) {
      add(key, `${prefix}: ${values.slice(0, 2).join(', ')}${values.length > 2 ? ` +${values.length - 2}` : ''}`, () =>
        patch({ [key]: undefined } as Partial<BookingFilters & QuoteFilters>)
      )
    }
  }

  // Surfaced as a chip because it WIDENS the result set — an operator who leaves
  // it on and forgets would silently include test bookings in a revenue figure.
  if (filters.includeTest) {
    add('includeTest', 'Including test bookings', () => patch({ includeTest: undefined }))
  }

  return chips
}
